import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../config/api.js';

// Normalize many common date inputs to ISO yyyy-mm-dd (or '' if invalid)
function toIsoDate(raw) {
  if (!raw) return '';
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(raw).trim();
  const parts = s.match(/\d+/g) || [];
  if (parts.length < 3) return '';

  let y;
  let mo;
  let d;

  if (parts[0].length === 4) {
    // YYYY-M-D
    y = Number(parts[0]);
    mo = Number(parts[1]);
    d = Number(parts[2]);
  } else if (parts[2].length === 4) {
    // M/D/YYYY or D/M/YYYY (decide by range)
    const n1 = Number(parts[0]);
    const n2 = Number(parts[1]);
    y = Number(parts[2]);

    if (n1 > 12) {
      d = n1;
      mo = n2;
    } else if (n2 > 12) {
      mo = n1;
      d = n2;
    } else {
      // default to MM/DD/YYYY
      mo = n1;
      d = n2;
    }
  } else {
    return '';
  }

  const pad = (num) => String(num).padStart(2, '0');
  const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
  if (!Number.isNaN(dt.getTime()) && dt.getUTCFullYear() === y && dt.getUTCMonth() + 1 === mo && dt.getUTCDate() === d) {
    return `${y}-${pad(mo)}-${pad(d)}`;
  }

  return '';
}

// Return today's yyyy-mm-dd (local)
function todayIso() {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Compare two yyyy-mm-dd strings
function isoAfter(a, b) {
  if (!a || !b) return false;
  return a > b;
}

const normalizeStats = (data = {}) => ({
  total: data.total ?? data.Total ?? data.allQueries ?? data.All ?? 0,
  initiated: data.initiated ?? data.Initiated ?? 0,
  completed: data.completed ?? data.Completed ?? 0,
  approved: data.approved ?? data.Approved ?? 0,
  refer: data.refer ?? data.Refer ?? 0,
  corrected: data.corrected ?? data.Corrected ?? 0,
  advised: data.advised ?? data.Advised ?? 0,
  closed: data.closed ?? data.Closed ?? 0,
  tatInitiated: data.tatInitiated ?? data.TATInitiated ?? data.tat_initiated ?? 0,
  accepted: data.accepted ?? data.Accepted ?? data.totalAccepted ?? 0,
  pending: data.pending ?? data.Pending ?? data.totalPending ?? data.totalUnAccepted ?? 0,
});

const formatValue = (value, { noneLabel = '0', pad2 = false } = {}) => {
  if (value === null || value === undefined || value === '') return noneLabel;
  const str = String(value);
  return pad2 ? str.padStart(2, '0') : str;
};

const STATS_ENDPOINT = 'agent-dashboard-routed-stats';

const BackofficeDashboardPanel = ({ isActive }) => {
  const [reviewFrom, setReviewFrom] = useState('');
  const [reviewTo, setReviewTo] = useState('');
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewErr, setReviewErr] = useState('');

  const [agentName, setAgentName] = useState('All');
  const [agentId, setAgentId] = useState('ALL');
  const [agentFrom, setAgentFrom] = useState('');
  const [agentTo, setAgentTo] = useState('');
  const [agentStats, setAgentStats] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentErr, setAgentErr] = useState('');
  const [agentOptions, setAgentOptions] = useState([
    { id: 'ALL', name: 'All' },
  ]);

  const departmentName = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.DepartmentName || user?.departmentName || user?.Department || user?.department || '';
    } catch {
      return '';
    }
  }, []);

  const reviewAbortRef = useRef(null);
  const agentAbortRef = useRef(null);

  useEffect(() => {
    if (reviewErr) setReviewErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFrom, reviewTo]);

  useEffect(() => {
    if (agentErr) setAgentErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFrom, agentTo, agentId, agentName]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (departmentName) {
          // API expects departmentName (case-sensitive). Keep legacy keys for safety.
          params.set('departmentName', departmentName);
          params.set('departmantname', departmentName);
          params.set('departmentname', departmentName);
        }
        const res = await fetch(`${API_BASE}/BoFillagents?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        });
        const text = await res.text();
        let json = null;
        try { json = text ? JSON.parse(text) : null; } catch {}
        if (!alive) return;

        const list =
          Array.isArray(json?.data?.data) ? json.data.data :
          Array.isArray(json?.data) ? json.data :
          Array.isArray(json?.Data) ? json.Data :
          Array.isArray(json?.agents) ? json.agents :
          Array.isArray(json?.result) ? json.result :
          Array.isArray(json) ? json : [];

        const options = list.map((row, idx) => {
          if (typeof row === 'string') {
            return { id: String(idx + 1), name: row.trim() };
          }
          const id = row?.id ?? row?.ID ?? row?.UserID ?? row?.userId ?? row?.userid ?? row?.agentId ?? row?.AgentID ?? `${idx + 1}`;
          const name =
            row?.FullName ?? row?.fullName ?? row?.fullname ?? row?.full_name ??
            row?.LoginName ?? row?.loginName ?? row?.login_name ??
            row?.UserName ?? row?.username ?? row?.user_name ??
            row?.AgentName ?? row?.agentName ??
            row?.Name ?? row?.name ??
            row?.Agent ?? row?.agent ??
            '';
          return { id: String(id), name: String(name || '').trim() };
        }).filter((opt) => opt.name && opt.name !== '--Select--' && opt.id !== '0');

        const unique = [];
        const seen = new Set();
        for (const opt of options) {
          const key = `${opt.id}-${opt.name}`.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          unique.push(opt);
        }

        const nextOptions = [
          { id: 'ALL', name: 'All' },
          ...unique
        ];
        setAgentOptions(nextOptions);
        if (!nextOptions.find(opt => String(opt.id) === String(agentId))) {
          setAgentId('ALL');
          setAgentName('All');
        }
      } catch {
        if (!alive) return;
        setAgentOptions([
          { id: 'ALL', name: 'All' },
        ]);
        setAgentId('ALL');
        setAgentName('All');
      }
    })();
    return () => { alive = false; };
  }, [agentId, departmentName]);

  const maxDay = todayIso();

  const validateDates = (fromRaw, toRaw, setErr) => {
    const fromIso = toIsoDate(fromRaw);
    const toIsoV = toIsoDate(toRaw);
    const today = todayIso();

    if (fromRaw && !fromIso) { setErr('Invalid "Date From".'); return null; }
    if (toRaw && !toIsoV) { setErr('Invalid "Date To".'); return null; }

    if (fromIso && isoAfter(fromIso, today)) { setErr("Date From can't be greater than today."); return null; }
    if (toIsoV && isoAfter(toIsoV, today)) { setErr("Date To can't be greater than today."); return null; }
    if (fromIso && toIsoV && isoAfter(fromIso, toIsoV)) { setErr('Date From cannot be after Date To.'); return null; }

    return { fromIso, toIsoV };
  };

  const fetchStats = async ({ fromDate, toDate, agent, agentIdValue, useAllQueriesTotal = false, setErr, setLoading, setStats, abortRef }) => {
    setErr('');

    const range = validateDates(fromDate, toDate, setErr);
    if (!range) return;
    const { fromIso, toIsoV } = range;

    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const params = new URLSearchParams();
    if (fromIso) params.set('dateFrom', fromIso);
    if (toIsoV) params.set('dateTo', toIsoV);
    if (agentIdValue && agentIdValue !== 'ALL' && agentIdValue !== '0') {
      params.set('agentId', agentIdValue);
    } else if (agent && agent !== 'All') {
      params.set('agentName', agent);
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/${STATS_ENDPOINT}?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: ac.signal,
      });
      const text = await res.text();
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch {}
      if (!res.ok) throw new Error(json?.message || text || 'Request failed');

      const data = json?.data || {};
      const next = normalizeStats(data);
      if (useAllQueriesTotal) {
        next.total = data.allQueries ?? data.AllQueries ?? next.total;
      }
      if (ac.signal.aborted) return;
      setStats(next);
    } catch (err) {
      if (ac.signal.aborted) return;
      setErr(err?.message || 'Failed to load dashboard stats');
    } finally {
      if (!ac.signal.aborted) setLoading(false);
      if (abortRef.current === ac) abortRef.current = null;
    }
  };

  const handleReviewShow = () => {
    fetchStats({
      fromDate: reviewFrom,
      toDate: reviewTo,
      agent: null,
      agentIdValue: null,
      useAllQueriesTotal: true,
      setErr: setReviewErr,
      setLoading: setReviewLoading,
      setStats: setReviewStats,
      abortRef: reviewAbortRef,
    });
  };

  const handleAgentShow = () => {
    fetchStats({
      fromDate: agentFrom,
      toDate: agentTo,
      agent: agentName,
      agentIdValue: agentId,
      setErr: setAgentErr,
      setLoading: setAgentLoading,
      setStats: setAgentStats,
      abortRef: agentAbortRef,
    });
  };

  return (
    <section className={`module-panel ${isActive ? 'active' : ''}`} id="module-dashboard">
      <section className="panel legacy-top reveal is-visible dashboard-theme">
        <div className="panel-head">
          <h3>Reviewers Dashboard</h3>
          <span className="status-chip">Review Overview</span>
        </div>
        <div className="my-dashboard-layout">
          <div className="agent-filter-panel slim">
            <label>
              <span>Date From</span>
              <input
                type="date"
                value={toIsoDate(reviewFrom) || reviewFrom}
                max={maxDay}
                onChange={(e) => setReviewFrom(e.target.value)}
              />
            </label>
            <label>
              <span>Date To</span>
              <input
                type="date"
                value={toIsoDate(reviewTo) || reviewTo}
                max={maxDay}
                onChange={(e) => setReviewTo(e.target.value)}
              />
            </label>
            <button className="btn btn-primary" type="button" onClick={handleReviewShow} disabled={reviewLoading}>
              {reviewLoading ? 'Loading…' : 'Show'}
            </button>
            {reviewErr && <div className="alert alert-danger mt-2 py-2">{reviewErr}</div>}
          </div>
          <div className="dashboard-stats">
            <div className="stats-banner">Reviewers Dashboard</div>
            <div className="highlight-grid cols-3">
              <article><span>All</span><strong>{formatValue(reviewStats?.total, { pad2: true })}</strong></article>
              <article><span>Completed</span><strong>{formatValue(reviewStats?.completed)}</strong></article>
              <article><span>Initiated</span><strong>{formatValue(reviewStats?.initiated)}</strong></article>
              <article><span>Accepted</span><strong>{formatValue(reviewStats?.accepted)}</strong></article>
              <article><span>Corrected</span><strong>{formatValue(reviewStats?.corrected)}</strong></article>
              <article><span>Approved</span><strong>{formatValue(reviewStats?.approved)}</strong></article>
              <article><span>Pending</span><strong>{formatValue(reviewStats?.pending)}</strong></article>
              <article><span>Advised</span><strong>{formatValue(reviewStats?.advised)}</strong></article>
              <article><span>Refer</span><strong>{formatValue(reviewStats?.refer)}</strong></article>
              <article><span>Closed</span><strong>{formatValue(reviewStats?.closed, { pad2: true })}</strong></article>
            </div>
          </div>
        </div>
      </section>

      <section className="panel legacy-top reveal is-visible dashboard-theme">
        <div className="panel-head">
          <h3>Selected Agent Dashboard</h3>
          <span className="status-chip">Today</span>
        </div>
        <div className="agent-dashboard-layout">
          <div className="agent-filter-panel">
            <label>
              <span>Agent</span>
              <select
                value={agentId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selected = agentOptions.find(opt => String(opt.id) === String(selectedId));
                  setAgentId(selectedId);
                  setAgentName(selected?.name || '--Select--');
                }}
              >
                {agentOptions.map((opt, idx) => (
                  <option key={`${opt.id}-${idx}`} value={opt.id}>{opt.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Date From</span>
              <input
                type="date"
                value={toIsoDate(agentFrom) || agentFrom}
                max={maxDay}
                onChange={(e) => setAgentFrom(e.target.value)}
              />
            </label>
            <label>
              <span>Date To</span>
              <input
                type="date"
                value={toIsoDate(agentTo) || agentTo}
                max={maxDay}
                onChange={(e) => setAgentTo(e.target.value)}
              />
            </label>
            <button className="btn btn-primary" type="button" onClick={handleAgentShow} disabled={agentLoading}>
              {agentLoading ? 'Loading…' : 'Show'}
            </button>
            {agentErr && <div className="alert alert-danger mt-2 py-2">{agentErr}</div>}
          </div>
          <div className="agent-stats-panel">
            <div className="stats-banner">Selected Agent's Dashboard</div>
            <div className="highlight-grid">
              <article><span>Total</span><strong>{formatValue(agentStats?.total, { pad2: true })}</strong></article>
              <article><span>Closed</span><strong>{formatValue(agentStats?.closed, { pad2: true })}</strong></article>
              <article><span>Initiated</span><strong>{formatValue(agentStats?.initiated, { noneLabel: 'None' })}</strong></article>
              <article><span>Completed</span><strong>{formatValue(agentStats?.completed, { noneLabel: 'None' })}</strong></article>
              <article><span>Approved</span><strong>{formatValue(agentStats?.approved, { noneLabel: 'None' })}</strong></article>
              <article><span>Corrected</span><strong>{formatValue(agentStats?.corrected, { noneLabel: 'None' })}</strong></article>
              <article><span>Refer</span><strong>{formatValue(agentStats?.refer, { noneLabel: 'None' })}</strong></article>
              <article><span>Advised</span><strong>{formatValue(agentStats?.advised, { noneLabel: 'None' })}</strong></article>
              <article><span>TAT Initiated</span><strong>{formatValue(agentStats?.tatInitiated, { pad2: true })}</strong></article>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default BackofficeDashboardPanel;
